/**
 * Signal values (ADR 0016) for the current person, from `GET /api/v1/signals`. Loaded when a page is opened
 * and when the app comes back to the foreground — never while a list is in view (no surprises, §9.19).
 * Definitions, rules and places come from the core doc; only the values live on the server.
 */
import { create } from "zustand";
import { useEffect } from "react";
import { useLocation } from "react-router";
import { apiCall } from "../core/api";
import type { SignalValue, Signals } from "../core/signals";

interface SignalsState {
  values: Record<string, Signals>; // by workspace
  loadedAt: Record<string, number>;
  load(workspaceId: string): Promise<void>;
}

export const useSignals = create<SignalsState>((set) => ({
  values: {},
  loadedAt: {},
  async load(workspaceId) {
    try {
      const r = await apiCall<{ values: Record<string, SignalValue> }>(workspaceId, "GET", "/signals");
      set((s) => ({
        values: { ...s.values, [workspaceId]: r.values },
        loadedAt: { ...s.loadedAt, [workspaceId]: Date.now() },
      }));
    } catch {
      // without values every condition is unknown, which changes nothing
    }
  },
}));

/** Keep the values fresh on page changes and when the app is shown again (AppShell). */
export function useSignalsLoader(workspaceId: string | null) {
  const { pathname } = useLocation();
  const load = useSignals((s) => s.load);
  useEffect(() => {
    if (workspaceId) void load(workspaceId);
  }, [workspaceId, pathname, load]);
  useEffect(() => {
    if (!workspaceId) return;
    const onShow = () => document.visibilityState === "visible" && void load(workspaceId);
    document.addEventListener("visibilitychange", onShow);
    return () => document.removeEventListener("visibilitychange", onShow);
  }, [workspaceId, load]);
}

const EMPTY: Signals = {};
export const useSignalValues = (workspaceId: string | null): Signals =>
  useSignals((s) => (workspaceId ? (s.values[workspaceId] ?? EMPTY) : EMPTY));
